import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Flame,
  FolderPlus,
  LayoutGrid,
  List,
  ListFilter,
  Pencil,
  Phone,
  PhoneCall,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { CustomFieldInput } from "./custom-field-input";
import { Modal } from "../../components/ui/modal";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { ActionMenu } from "../../components/ui/action-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { useToast } from "../../components/ui/toast";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import { DatePicker } from "../../components/ui/date-picker";
import { Dropdown } from "../../components/ui/dropdown";
import { CampaignModal } from "./campaign-modal";
import {
  WEEKDAYS,
  type CampaignField,
  type CampaignMetrics,
  type CampaignSchedule,
  type CampaignWindow,
  type LeadCampaign,
} from "./types";

const CrmPopoverContent = PopoverContent as any;

const fieldCls =
  "w-full rounded-lg border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-3 py-2 text-sm text-ink dark:text-stone-100 outline-none focus:border-brand";
const labelCls = "mb-1.5 block text-xs font-semibold text-ink-2 dark:text-stone-300";

const BLANK_WINDOW: CampaignWindow = { days: [0, 1, 2, 3, 4], start: "09:00", end: "18:00" };

function ScheduleEditor({
  initial,
  onSave,
  saving,
  error,
}: {
  initial: CampaignSchedule | null;
  onSave: (schedule: CampaignSchedule | null) => Promise<void>;
  saving: boolean;
  error: string;
}) {
  const [active, setActive] = useState(initial?.active ?? true);
  const [timezone, setTimezone] = useState(initial?.timezone ?? "Asia/Kolkata");
  const [startDate, setStartDate] = useState(initial?.startDate ?? "");
  const [endDate, setEndDate] = useState(initial?.endDate ?? "");
  const [windows, setWindows] = useState<CampaignWindow[]>(
    initial?.windows?.length ? initial.windows.map((w) => ({ ...w, days: [...w.days] })) : [{ ...BLANK_WINDOW }],
  );

  const toggleDay = (index: number, day: number) =>
    setWindows((prev) =>
      prev.map((w, i) =>
        i === index
          ? { ...w, days: w.days.includes(day) ? w.days.filter((d) => d !== day) : [...w.days, day].sort() }
          : w,
      ),
    );
  const patchWindow = (index: number, patch: Partial<CampaignWindow>) =>
    setWindows((prev) => prev.map((w, i) => (i === index ? { ...w, ...patch } : w)));

  const submit = async () => {
    const cleaned = windows
      .map((w) => ({ days: [...new Set(w.days)].sort(), start: w.start, end: w.end }))
      .filter((w) => w.days.length > 0 && w.start && w.end);
    await onSave({
      timezone: timezone.trim() || "Asia/Kolkata",
      active,
      startDate: startDate || null,
      endDate: endDate || null,
      windows: cleaned,
    });
  };

  return (
    <div className="space-y-4">
      <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-2 dark:text-stone-300">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 accent-brand rounded"
        />
        Campaign calling enabled
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Timezone</label>
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="Asia/Kolkata"
            className={fieldCls}
          />
        </div>
        <div>
          <label className={labelCls}>Start date</label>
          <DatePicker
            value={startDate}
            onChange={(val) => setStartDate(val)}
            placeholder="Select start date"
            allowClear
            className="w-full"
          />
        </div>
        <div>
          <label className={labelCls}>End date</label>
          <DatePicker
            value={endDate}
            onChange={(val) => setEndDate(val)}
            placeholder="Select end date"
            minDate={startDate}
            allowClear
            className="w-full"
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Calling windows</label>
        <div className="space-y-3">
          {windows.map((w, i) => (
            <div key={i} className="rounded-lg border border-line dark:border-white/15 p-3 bg-stone-100 dark:bg-stone-800/40">
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((label, day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(i, day)}
                    className={cn(
                      "rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors",
                      w.days.includes(day)
                        ? "bg-brand text-white"
                        : "bg-soft dark:bg-stone-700 text-ink-3 dark:text-stone-300 hover:bg-line dark:hover:bg-stone-600",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <input
                  type="time"
                  aria-label="Window start"
                  value={w.start}
                  onChange={(e) => patchWindow(i, { start: e.target.value })}
                  className={fieldCls}
                />
                <span className="text-xs text-ink-3 dark:text-stone-400">to</span>
                <input
                  type="time"
                  aria-label="Window end"
                  value={w.end}
                  onChange={(e) => patchWindow(i, { end: e.target.value })}
                  className={fieldCls}
                />
                <button
                  type="button"
                  onClick={() => setWindows((prev) => prev.filter((_, j) => j !== i))}
                  className="shrink-0 rounded-lg px-2.5 py-2 text-xs font-semibold text-ink-3 dark:text-stone-400 hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setWindows((prev) => [...prev, { ...BLANK_WINDOW }])}
            className="text-xs font-semibold text-brand hover:underline"
          >
            + Add window
          </button>
        </div>
        <p className="mt-1.5 text-xs text-ink-3 dark:text-stone-400">
          No windows means open all day. Backend rejects end-before-start and bad dates.
        </p>
      </div>
      {error && <p role="alert" className="text-xs font-semibold text-red-500">{error}</p>}
      <button
        type="button"
        disabled={saving}
        onClick={() => void submit()}
        className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50 shadow-xs"
      >
        {saving ? "Saving…" : "Save schedule"}
      </button>
    </div>
  );
}

export function CampaignsPage() {
  const cache = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  const canManage = team.data?.role === "admin" || !!team.data?.permissions?.assign_leads;
  const metrics = useQuery({
    queryKey: ["crm-campaign-metrics"],
    queryFn: crmApi.campaignMetrics,
    enabled: canManage,
    refetchInterval: 60000,
  });
  const campaigns = useQuery({ queryKey: ["crm-campaigns"], queryFn: crmApi.campaigns, enabled: canManage });
  const [editing, setEditing] = useState<CampaignMetrics | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<LeadCampaign | null>(null);
  const [previewing, setPreviewing] = useState<LeadCampaign | null>(null);
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState<"" | "open" | "closed">("");
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({ ...presetRange("last_30"), preset: "last_30" }));
  const [sort, setSort] = useState<"latest_created" | "oldest_created" | "recently_modified" | "oldest_modified">("latest_created");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const refresh = () =>
    Promise.all([
      cache.invalidateQueries({ queryKey: ["crm-campaign-metrics"] }),
      cache.invalidateQueries({ queryKey: ["crm-campaigns"] }),
    ]);

  const saveSchedule = async (schedule: CampaignSchedule | null) => {
    if (!editing || saving) return;
    const campaign = campaigns.data?.find((c) => c.id === editing.id);
    if (!campaign) {
      setError("Campaign not found — refresh and retry.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await crmApi.updateCampaign(editing.id, campaign.name, campaign.fields ?? [], schedule);
      await refresh();
      toast.success("Schedule saved");
      setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const createCampaign = async (name: string, fields: CampaignField[]) => {
    await crmApi.createCampaign(name, fields);
    await refresh();
    toast.success("Campaign created");
  };

  const saveForm = async (name: string, fields: CampaignField[]) => {
    if (!editingForm) return;
    await crmApi.updateCampaign(editingForm.id, name, fields);
    await refresh();
    toast.success("Campaign form updated");
    setEditingForm(null);
  };

  const [deleting, setDeleting] = useState<CampaignMetrics | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const confirmDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await crmApi.deleteCampaign(deleting.id);
      await refresh();
      toast.success("Campaign deleted");
      setDeleting(null);
    } catch (e) {
      toast.danger("Couldn't delete campaign", e instanceof Error ? e.message : "Please retry.");
    } finally {
      setDeleteBusy(false);
    }
  };

  // Executive KPI summary across all campaigns
  const summaryKpis = useMemo(() => {
    const list = metrics.data ?? [];
    const totalCampaigns = list.length;
    const openCampaigns = list.filter((r) => r.openNow).length;
    const totalLeads = list.reduce((acc, r) => acc + (r.leads || 0), 0);
    const totalCalls = list.reduce((acc, r) => acc + (r.calls || 0), 0);
    const totalConnected = list.reduce((acc, r) => acc + (r.connected || 0), 0);
    const totalInterested = list.reduce((acc, r) => acc + (r.interested || 0), 0);
    const totalConverted = list.reduce((acc, r) => acc + (r.converted || 0), 0);
    const totalOpenFollowUps = list.reduce((acc, r) => acc + (r.openFollowUps || 0), 0);
    const connectRate = totalCalls > 0 ? Math.round((totalConnected / totalCalls) * 100) : 0;
    const conversionRate = totalLeads > 0 ? Math.round((totalConverted / totalLeads) * 100) : 0;

    return {
      totalCampaigns,
      openCampaigns,
      totalLeads,
      totalCalls,
      totalConnected,
      totalInterested,
      totalConverted,
      totalOpenFollowUps,
      connectRate,
      conversionRate,
    };
  }, [metrics.data]);

  const displayedCampaigns = useMemo(() => {
    const query = search.trim().toLowerCase();
    const end = new Date(dateRange.end);
    end.setHours(23, 59, 59, 999);
    const filtered = (metrics.data ?? []).filter(
      (row) =>
        (!query || row.name.toLowerCase().includes(query)) &&
        (!availability || (availability === "open" ? row.openNow : !row.openNow)) &&
        (() => {
          const createdAt = campaigns.data?.find((campaign) => campaign.id === row.id)?.createdAt;
          if (!createdAt) return false;
          const created = new Date(createdAt);
          return !Number.isNaN(created.getTime()) && created >= dateRange.start && created <= end;
        })(),
    );
    const timestamp = (row: CampaignMetrics, modified = false) => {
      const campaign = campaigns.data?.find((item) => item.id === row.id);
      return new Date((modified ? campaign?.updatedAt : campaign?.createdAt) || campaign?.createdAt || 0).getTime();
    };
    return filtered.sort((left, right) => {
      if (sort === "oldest_created") return timestamp(left) - timestamp(right);
      if (sort === "recently_modified") return timestamp(right, true) - timestamp(left, true);
      if (sort === "oldest_modified") return timestamp(left, true) - timestamp(right, true);
      return timestamp(right) - timestamp(left);
    });
  }, [metrics.data, campaigns.data, search, availability, dateRange, sort]);

  const createdDate = (campaign?: LeadCampaign) => {
    if (!campaign?.createdAt) return "Created date unavailable";
    const date = new Date(campaign.createdAt);
    return Number.isNaN(date.getTime())
      ? "Created date unavailable"
      : `Created ${date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  };

  const scheduleSummary = (row: CampaignMetrics): string => {
    if (!row.schedule) return "Always open";
    if (!row.schedule.active) return "Paused";
    const parts = [];
    if (row.schedule.startDate || row.schedule.endDate)
      parts.push(`${row.schedule.startDate || "…"} → ${row.schedule.endDate || "…"}`);
    if ((row.schedule.windows ?? []).length > 0) parts.push(`${row.schedule.windows.length} window(s)`);
    return parts.join(" · ") || "Always open";
  };

  return (
    <div className="flex flex-col p-4 sm:p-5 md:p-6 space-y-4 w-full">
      {/* 1. Modern Compact Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-ink dark:text-white">Campaigns</h1>
        </div>
        {canManage && (
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs active:scale-95 transition-all"
          >
            <FolderPlus className="h-3.5 w-3.5" /> New campaign
          </button>
        )}
      </div>

      {!canManage && team.data ? (
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-6 text-sm text-ink-2 dark:text-stone-300">
          Campaign management is available to sales managers and business admins.
        </div>
      ) : (
        <>
          {metrics.error && <p role="alert" className="text-xs font-semibold text-red-600">{(metrics.error as Error).message}</p>}
          {metrics.isLoading && <p className="text-xs text-ink-3 dark:text-stone-400">Loading campaigns…</p>}

          {/* 2. Executive KPI Summary Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Campaigns</span>
                <Sparkles className="h-3.5 w-3.5 text-brand" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalCampaigns}</span>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">({summaryKpis.openCampaigns} Open)</span>
              </div>
            </div>

            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Total Leads</span>
                <Users className="h-3.5 w-3.5 text-sky-500" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalLeads.toLocaleString()}</span>
              </div>
            </div>

            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Calls Placed</span>
                <PhoneCall className="h-3.5 w-3.5 text-amber-500" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalCalls.toLocaleString()}</span>
                <span className="text-xs text-ink-3 dark:text-stone-400">calls</span>
              </div>
            </div>

            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Connect Rate</span>
                <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{summaryKpis.connectRate}%</span>
                <span className="text-xs text-ink-3 dark:text-stone-400 font-mono">({summaryKpis.totalConnected})</span>
              </div>
            </div>

            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Converted</span>
                <CheckCircle2 className="h-3.5 w-3.5 text-purple-500" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalConverted}</span>
                <span className="text-xs text-purple-600 dark:text-purple-400 font-semibold">({summaryKpis.conversionRate}%)</span>
              </div>
            </div>

            <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
                <span className="font-medium">Open Follow-ups</span>
                <CalendarClock className="h-3.5 w-3.5 text-orange-500" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold text-ink dark:text-white">{summaryKpis.totalOpenFollowUps}</span>
                <span className="text-xs text-amber-600 dark:text-amber-400 font-semibold">pending</span>
              </div>
            </div>
          </div>

          {/* 3. Controls & Filter Strip */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3 shadow-card dark:shadow-card-dark">
            <div className="relative min-w-[220px] flex-1 max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search campaigns..."
                className="w-full rounded-lg border border-line dark:border-white/20 bg-stone-100 dark:bg-stone-800 py-1.5 pl-9 pr-3 text-xs sm:text-sm text-ink dark:text-stone-100 placeholder:text-ink-faint dark:placeholder:text-stone-400 outline-none focus:border-brand"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Availability Filter Popover */}
              <Popover open={availabilityOpen} onOpenChange={setAvailabilityOpen}>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg border border-line dark:border-white/15 bg-soft dark:bg-stone-800 px-3 py-1.5 text-xs font-medium text-ink dark:text-stone-200 hover:bg-line dark:hover:bg-stone-700 transition-colors",
                      availability && "border-brand-soft bg-brand-soft text-brand dark:bg-brand/20 dark:text-brand",
                    )}
                  >
                    <ListFilter className="h-3.5 w-3.5" />
                    Filter{availability ? `: ${availability === "open" ? "Open" : "Closed"}` : ""}
                  </button>
                </PopoverTrigger>
                <CrmPopoverContent
                  sideOffset={4}
                  className="w-[190px] overflow-hidden rounded-xl border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-0 shadow-lg"
                  align="end"
                >
                  <p className="px-3 pb-1 pt-3 text-xs font-semibold text-ink-3 dark:text-stone-400">Availability</p>
                  {(["open", "closed"] as const).map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setAvailability(option);
                        setAvailabilityOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors hover:bg-soft dark:hover:bg-stone-800",
                        availability === option ? "font-semibold text-brand" : "text-ink-2 dark:text-stone-300",
                      )}
                    >
                      {option === "open" ? "Open now" : "Closed"}
                      {availability === option && <span className="ml-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />}
                    </button>
                  ))}
                  {availability && (
                    <button
                      onClick={() => {
                        setAvailability("");
                        setAvailabilityOpen(false);
                      }}
                      className="w-full border-t border-line dark:border-white/10 px-3 py-1.5 text-left text-[11px] font-semibold text-ink-3 dark:text-stone-400 hover:text-brand"
                    >
                      Clear filter
                    </button>
                  )}
                </CrmPopoverContent>
              </Popover>

              {/* Date Range Picker */}
              <DateRangePicker value={dateRange} onChange={setDateRange} align="right" />

              {/* Sort Dropdown */}
              <Dropdown
                ariaLabel="Sort campaigns"
                value={sort}
                onChange={setSort}
                className="w-36 sm:w-44 text-xs py-1.5 bg-soft dark:bg-stone-800 border-line dark:border-white/15 text-ink dark:text-stone-200"
                options={[
                  { value: "latest_created", label: "Latest date" },
                  { value: "oldest_created", label: "Oldest date" },
                  { value: "recently_modified", label: "Recently modified" },
                  { value: "oldest_modified", label: "Oldest modified" },
                ]}
              />

              {/* View Switcher: Grid vs Table */}
              <div className="flex items-center rounded-lg border border-line dark:border-white/15 bg-soft dark:bg-stone-800 p-0.5 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  title="Card Grid view"
                  className={cn(
                    "flex items-center justify-center rounded-md p-1.5 transition-all",
                    viewMode === "grid"
                      ? "bg-elevated dark:bg-stone-700 text-ink dark:text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
                  )}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  title="Dense Table view"
                  className={cn(
                    "flex items-center justify-center rounded-md p-1.5 transition-all",
                    viewMode === "table"
                      ? "bg-elevated dark:bg-stone-700 text-ink dark:text-white shadow-xs"
                      : "text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
                  )}
                >
                  <List className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Reset Filters / Refresh */}
              <button
                title="Reset filters"
                aria-label="Reset filters"
                onClick={() => {
                  setSearch("");
                  setAvailability("");
                  setDateRange({ ...presetRange("last_30"), preset: "last_30" });
                  setSort("latest_created");
                  void refresh();
                }}
                className="rounded-lg border border-line dark:border-white/15 bg-soft dark:bg-stone-800 p-1.5 text-ink-2 dark:text-stone-300 transition-colors hover:bg-line dark:hover:bg-stone-700 hover:text-ink dark:hover:text-white"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* 4. Campaigns View (Grid or Dense Table) */}
          {viewMode === "grid" ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {displayedCampaigns.map((row) => {
                const campaign = campaigns.data?.find((item) => item.id === row.id);
                const connectRate = row.calls > 0 ? Math.round((row.connected / row.calls) * 100) : 0;
                const conversionRate = row.leads > 0 ? Math.round((row.converted / row.leads) * 100) : 0;

                return (
                  <div
                    key={row.id}
                    className="flex flex-col justify-between rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-[#201e1b] p-4 sm:p-5 shadow-xs transition-colors hover:border-stone-300 dark:hover:border-stone-700"
                  >
                    <div>
                      {/* Card Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h2
                            onClick={() => navigate(`/crm/dialer?campaignId=${row.id}`)}
                            title="Open in Dialer"
                            className="truncate text-base font-semibold text-stone-900 dark:text-stone-100 hover:text-brand cursor-pointer transition-colors"
                          >
                            {row.name}
                          </h2>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-stone-400" />
                              {scheduleSummary(row)}
                            </span>
                            <span className="text-stone-300 dark:text-stone-600">·</span>
                            <span>{createdDate(campaign)}</span>
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border",
                              row.openNow
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50"
                                : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700",
                            )}
                          >
                            <span
                              className={cn(
                                "h-1.5 w-1.5 rounded-full",
                                row.openNow ? "bg-emerald-500" : "bg-stone-400",
                              )}
                            />
                            {row.openNow ? "Open now" : "Closed"}
                          </span>

                          <div className="[&>button]:opacity-100">
                            <ActionMenu
                              ariaLabel={`${row.name} actions`}
                              items={[
                                { label: "Start Dialing", onClick: () => navigate(`/crm/dialer?campaignId=${row.id}`) },
                                { label: "View Leads", onClick: () => navigate(`/crm/leads?campaignId=${row.id}`) },
                                ...(campaign
                                  ? [
                                      {
                                        label: "Preview form",
                                        onClick: () => {
                                          setPreviewing(campaign);
                                        },
                                      },
                                      {
                                        label: "Share link",
                                        onClick: async () => {
                                          const url = `${window.location.origin}${campaign.form_url || `/forms/crm/${campaign.token || campaign.id}`}`;
                                          try {
                                            await navigator.clipboard.writeText(url);
                                            toast.success("Form link copied");
                                          } catch {
                                            setPreviewing(campaign);
                                          }
                                        },
                                      },
                                      { label: "Edit form", onClick: () => setEditingForm(campaign) },
                                    ]
                                  : []),
                                { label: "Delete", danger: true, onClick: () => setDeleting(row) },
                              ]}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Unified Metrics Panel */}
                      <div className="mt-3.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/70 dark:bg-stone-900/40 overflow-hidden divide-y divide-stone-200 dark:divide-stone-800">
                        {/* Call Activity Funnel */}
                        <div className="grid grid-cols-4 divide-x divide-stone-200 dark:divide-stone-800 py-2.5">
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Leads</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{row.leads}</div>
                          </div>
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Calls</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{row.calls}</div>
                          </div>
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Connected</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{row.connected}</div>
                          </div>
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Connect %</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{connectRate}%</div>
                          </div>
                        </div>

                        {/* Outcomes & Conversion */}
                        <div className="grid grid-cols-4 divide-x divide-stone-200 dark:divide-stone-800 py-2.5">
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Interested</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{row.interested}</div>
                          </div>
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Follow-ups</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{row.openFollowUps}</div>
                          </div>
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Converted</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{row.converted}</div>
                          </div>
                          <div className="min-w-0 px-1 text-center">
                            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-stone-400 dark:text-stone-400">Conv. %</div>
                            <div className="mt-1 text-base font-semibold tabular-nums text-stone-900 dark:text-stone-100">{conversionRate}%</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Actions Bottom Strip — always 2x2: Start Dialing | Leads / Schedule | Assign */}
                    <div className="mt-4 pt-3 border-t border-stone-200 dark:border-stone-800 min-w-0 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => navigate(`/crm/dialer?campaignId=${row.id}`)}
                        title="Open in Power Dialer"
                        className="inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all"
                      >
                        <Play className="h-3 w-3 shrink-0 fill-white" />
                        <span className="truncate">Start Dialing</span>
                      </button>
                      <button
                        onClick={() => navigate(`/crm/leads?campaignId=${row.id}`)}
                        title="View leads for this campaign"
                        className="inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 px-2.5 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-700 active:scale-[0.98] transition-all"
                      >
                        <Users className="h-3 w-3 shrink-0 text-stone-400 dark:text-stone-400" />
                        <span className="truncate">Leads</span>
                      </button>
                      <button
                        onClick={() => {
                          setError("");
                          setEditing(row);
                        }}
                        className="inline-flex min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-xs font-medium text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                      >
                        <Pencil className="h-3 w-3 shrink-0" />
                        <span className="truncate">Schedule</span>
                      </button>
                      <button
                        onClick={() => navigate("/crm/assignment")}
                        className="inline-flex min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-xs font-medium text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                      >
                        <CalendarClock className="h-3 w-3 shrink-0" />
                        <span className="truncate">Assign</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Table View */
            <div className="rounded-2xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] shadow-card dark:shadow-card-dark overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-stone-100 dark:bg-stone-800/80 text-xs font-semibold uppercase tracking-wider text-ink-2 dark:text-stone-300 border-b border-line dark:border-white/15">
                    <tr>
                      <th className="p-3.5 pl-4">Campaign</th>
                      <th className="p-3.5">Schedule</th>
                      <th className="p-3.5 text-center">Leads</th>
                      <th className="p-3.5 text-center">Calls</th>
                      <th className="p-3.5 text-center">Connect %</th>
                      <th className="p-3.5 text-center">Interested</th>
                      <th className="p-3.5 text-center">Converted</th>
                      <th className="p-3.5 text-center">Follow-ups</th>
                      <th className="p-3.5 pr-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line dark:divide-white/10">
                    {displayedCampaigns.map((row) => {
                      const campaign = campaigns.data?.find((item) => item.id === row.id);
                      const connectRate = row.calls > 0 ? Math.round((row.connected / row.calls) * 100) : 0;

                      return (
                        <tr
                          key={row.id}
                          className="hover:bg-stone-100 dark:hover:bg-stone-800/50 transition-colors group"
                        >
                          <td className="p-3.5 pl-4 align-middle">
                            <div>
                              <p
                                onClick={() => navigate(`/crm/dialer?campaignId=${row.id}`)}
                                className="font-semibold text-ink dark:text-stone-100 hover:text-brand cursor-pointer"
                              >
                                {row.name}
                              </p>
                              <p className="text-xs text-ink-3 dark:text-stone-400">{createdDate(campaign)}</p>
                            </div>
                          </td>
                          <td className="p-3.5 align-middle text-xs">
                            <span
                              className={cn(
                                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border",
                                row.openNow
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700/60"
                                  : "bg-soft dark:bg-stone-800 text-ink-3 dark:text-stone-400 border-line dark:border-white/10",
                              )}
                            >
                              <span
                                className={cn(
                                  "h-1.5 w-1.5 rounded-full",
                                  row.openNow ? "bg-emerald-500 animate-pulse" : "bg-stone-400",
                                )}
                              />
                              {row.openNow ? "Open" : "Closed"}
                            </span>
                            <p className="text-[11px] text-ink-3 dark:text-stone-400 mt-0.5 truncate max-w-[140px]">
                              {scheduleSummary(row)}
                            </p>
                          </td>
                          <td className="p-3.5 align-middle text-center font-bold text-ink dark:text-stone-100">
                            {row.leads}
                          </td>
                          <td className="p-3.5 align-middle text-center font-bold text-ink dark:text-stone-100">
                            {row.calls}
                          </td>
                          <td className="p-3.5 align-middle text-center">
                            <span className="inline-flex rounded-md bg-sky-500/10 px-2 py-0.5 text-xs font-bold text-sky-700 dark:text-sky-300">
                              {connectRate}%
                            </span>
                          </td>
                          <td className="p-3.5 align-middle text-center font-semibold text-amber-600 dark:text-amber-400">
                            {row.interested}
                          </td>
                          <td className="p-3.5 align-middle text-center font-semibold text-emerald-600 dark:text-emerald-400">
                            {row.converted}
                          </td>
                          <td className="p-3.5 align-middle text-center font-semibold text-purple-600 dark:text-purple-400">
                            {row.openFollowUps}
                          </td>
                          <td className="p-3.5 pr-4 text-right align-middle">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => navigate(`/crm/dialer?campaignId=${row.id}`)}
                                title="Start Dialing"
                                className="flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover"
                              >
                                <Play className="h-3 w-3 fill-white" /> Dial
                              </button>
                              <button
                                onClick={() => navigate(`/crm/leads?campaignId=${row.id}`)}
                                title="View Leads"
                                className="flex items-center gap-1 rounded-md border border-line dark:border-white/15 bg-soft dark:bg-stone-800 px-2 py-1 text-xs font-semibold text-ink-2 dark:text-stone-200 hover:bg-line dark:hover:bg-stone-700"
                              >
                                Leads
                              </button>
                              <button
                                onClick={() => {
                                  setError("");
                                  setEditing(row);
                                }}
                                title="Schedule"
                                className="rounded-md p-1.5 text-ink-3 dark:text-stone-400 hover:bg-soft dark:hover:bg-stone-800 hover:text-ink dark:hover:text-white"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <ActionMenu
                                ariaLabel={`${row.name} actions`}
                                items={[
                                  ...(campaign
                                    ? [
                                        {
                                          label: "Preview form",
                                          onClick: () => {
                                            setPreviewing(campaign);
                                          },
                                        },
                                        {
                                          label: "Share link",
                                          onClick: async () => {
                                            const url = `${window.location.origin}${campaign.form_url || `/forms/crm/${campaign.token || campaign.id}`}`;
                                            try {
                                              await navigator.clipboard.writeText(url);
                                              toast.success("Form link copied");
                                            } catch {
                                              setPreviewing(campaign);
                                            }
                                          },
                                        },
                                        { label: "Edit form", onClick: () => setEditingForm(campaign) },
                                      ]
                                    : []),
                                  { label: "Delete", danger: true, onClick: () => setDeleting(row) },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {displayedCampaigns.length === 0 && !metrics.isLoading && (
            <div className="rounded-2xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-8 text-center text-sm text-ink-3 dark:text-stone-400">
              No campaigns match your filter criteria.
            </div>
          )}
        </>
      )}

      {deleting && (
        <ConfirmModal
          title="Delete campaign"
          body={`Delete "${deleting.name}"? Only empty campaigns can be removed — move or delete its leads first.`}
          confirmLabel={deleteBusy ? "Deleting…" : "Delete"}
          onClose={() => !deleteBusy && setDeleting(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}

      {editing && (
        <Modal
          title={`Schedule — ${editing.name}`}
          subtitle="When this campaign may be called. Empty means no constraint."
          onClose={() => !saving && setEditing(null)}
          width="max-w-lg"
          footer={
            <button
              onClick={() => !saving && setEditing(null)}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 dark:text-stone-300 hover:bg-soft dark:hover:bg-stone-800"
            >
              Close
            </button>
          }
        >
          <ScheduleEditor
            key={editing.id + JSON.stringify(editing.schedule ?? null)}
            initial={editing.schedule ?? null}
            onSave={saveSchedule}
            saving={saving}
            error={error}
          />
        </Modal>
      )}

      {createOpen && (
        <CampaignModal existing={campaigns.data ?? []} onClose={() => setCreateOpen(false)} onSave={createCampaign} />
      )}

      {editingForm && (
        <CampaignModal
          existing={campaigns.data ?? []}
          campaign={editingForm}
          onClose={() => setEditingForm(null)}
          onSave={saveForm}
        />
      )}

      {previewing && (
        <Modal
          title={`Preview form — ${previewing.name}`}
          subtitle="How leads will see this form."
          onClose={() => setPreviewing(null)}
          width="max-w-xl"
          footer={
            <button
              onClick={() => setPreviewing(null)}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 dark:text-stone-300 hover:bg-soft dark:hover:bg-stone-800"
            >
              Close
            </button>
          }
        >
          <div className="space-y-4">
            {/* Live form preview — same layout as /forms/crm/:token */}
            <div className="rounded-2xl border border-line dark:border-white/15 bg-soft dark:bg-stone-900/40 p-6 sm:p-8">
              <p className="mb-5 text-sm font-bold text-brand">Techladder.ai</p>
              <p className="mb-5 rounded-lg bg-elevated dark:bg-stone-800 p-3 text-xs text-ink-2 dark:text-stone-300">
                Your response is sent securely to the team that shared this form.
              </p>
              <h3 className="text-sm font-semibold text-ink dark:text-white">{previewing.name}</h3>
              {(previewing.fields ?? []).length === 0 ? (
                <p className="mt-4 text-sm text-ink-3">No questions added yet — use Edit form to add fields.</p>
              ) : (
                <div className="mt-2">
                  {(previewing.fields ?? []).map((field) => (
                    <div key={field.id} className="border-b border-line dark:border-white/10 py-5 text-sm font-semibold text-ink dark:text-stone-100">
                      <p className="mb-2">
                        {field.label}
                        {field.required ? " *" : ""}
                      </p>
                      <div className="pointer-events-none select-none" aria-hidden>
                        <CustomFieldInput
                          field={field}
                          value=""
                          onChange={() => {}}
                          inputClassName="mt-0 w-full rounded-lg border border-line dark:border-white/15 bg-elevated dark:bg-stone-800 px-3 py-2.5 font-normal text-ink dark:text-stone-100"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    disabled
                    className="mt-5 w-full cursor-default rounded-lg bg-brand px-4 py-3 font-semibold text-white opacity-90"
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
